import {
    ApplicationStatusType,
    BuildRentType,
    LoanType,
    LoanSubType,
    GCRoleType,
    GCStatusType,
    ExperienceType,
    ExperienceProjectType,
    ExperienceVerificationStatusType,
    ExperiencePropertyType,
    ExperienceDateType,
    ExperiencePropertyTwoToFourUnitStructureType,
    MonthsToAccessFundingType,
} from '@apply/constants';
import { BfiApi, ApiResponse, IApiResponse } from './bfiApi';
import { AttachmentHandleMode } from '../../../../apps/bfi-apply/src/app/application_v2/components/FileOrFormQuestions/types/AttachmentHandleMode';
import { AttachmentType } from '../../../../apps/bfi-apply/src/app/application_v2/components/FileOrFormQuestions/types/AttachmentType';

export interface Property {
    id: string;
    street1: string;
    street2?: string;
    city: string;
}

export interface IDashboardApplicationItem {
    applicationId: number;
    appId: string;
    properties: Property[];
    missingFiles: string[];

    percentComplete: number;
    createdAt: Date;
    applicationStatus: ApplicationStatusType;
    pausePoint: string | null;
}

export interface IUserNoticeCount {
    total: number;
    unread: number;
}

export interface IBorrowerInfo {
    isFirstLoan: boolean;
    shouldAskLegalQuestions: boolean;
}

export interface IApplicationData {
    entryGuid: string;
    email: string;
    information: IApplicationInformation;
    complete: boolean;
    pausePoint: string | null;
}

export interface IApplicationInformation {
    borrower: IApplicationBorrower;
    properties: IApplicationProperties;
    pausePoint: string | null;
}

export interface IApplicationBorrower {
    id: number;
    apiId: string;
    isDirty: boolean;
    loanType: LoanType | undefined;
    loanSubType: LoanSubType | undefined;
    buildRentTypeId: BuildRentType | null;
    firstLoanApplicationWithBfi: boolean | undefined;
    howDidYouHear: string;
    currentlyQuestions: ICurrentlyQuestions;
    usingPropertyManagementCompany: boolean | undefined;
    propertyManagementCompany: IPropertyManagementCompany;
    principal: IApplicationPrincipal;
    entity: IApplicationEntity;
    esign: IApplicationEsign;
    guestAcknowledgedNoPersonalLoans: boolean;
    finalizeThirdPartyName: string | undefined;
    finalizeThirdPartyClaim: boolean | undefined;
    isFirstLoan: boolean;
    shouldAskLegalQuestions: boolean;
    masterNoteId: number | undefined;
}

export interface ICurrentlyQuestions {
    defendantInLegalActions: boolean | undefined;
    involvedInJudgements: boolean | undefined;
    delinquentOnDebts: boolean | undefined;
    delinquentOnTaxes: boolean | undefined;
    financiallyWorseOff: boolean | undefined;
    explanations: string | undefined;
}

export interface IPropertyManagementCompany {
    name: string | undefined;
    website: string | undefined;
    contactEmail: string | undefined;
    contactPhone: string | undefined;
    managementAgreement: {
        handleMode: AttachmentHandleMode;
        uploadedFilename: string | undefined;
        uploadedFileApiId: string | undefined;
    };
}

export interface IApplicationPrincipal {
    name: {
        first: string;
        middle: string;
        last: string;
    };
    email: string;
    phone: string;
    secondaryEmail: string | null;
}

export interface IApplicationEntity {
    id: number;
    name: string;
    stateIncorporated: string;
    typeId: number;
    address: {
        street1: string;
        street2: string | null;
        city: string;
        state: string;
        zipCode: string;
        county: string | null;
    };
}

export interface IApplicationEsign {
    typedName: string;
    title: string;
    date: string;
    acknowledgeFont: number | null;
    acknowledgeFontName: string;
    eSignFonts: {
        acknowledgeFont: number | null;
        eSignFont: string | null;
        termSheetFont: number | null;
        typedName: string | null;
        title: string | null;
    };
    termsheetFont: number | null;
    termsheetFontName: string;
    ipAddress: string;
}

export interface IApplicationProperties {
    isDirty: boolean;
    currentPropertyId: string | null;
    items: IApplicationProperty[];
    forms: (IBudgetConstruction | IBudgetFixFlip | IBudgetLand | ISpecSheet)[];
}

export interface IApplicationProperty {
    guid: string;
    isInitialCreate: boolean;
    structureType: string | undefined;
    address: {
        street1: string;
        street2: string;
        city: string;
        state: string;
        zipCode: string;
        county: string;
    };
    nonRental: IApplicationNonRental | null;
    rental: IApplicationRental | null;
    rentalUnits: IApplicationRentalUnits[];
}

export interface IApplicationNonRental {
    legalDescription: string;
    budget: IApplicationFile;
    fixFlipBudget: IApplicationFile;
    landBudget: IApplicationFile;
    plans: IApplicationFile;
    specSheet: IApplicationFile;
    survey: IApplicationFile;
    presaleContract: IApplicationFile;
    generalContractorResume: IApplicationFile;
    preSold: boolean | undefined;
    contactIsPrimary: boolean | undefined;
    email: string;
    name: string | undefined;
    contactName: {
        first: string | undefined;
        middle: string | undefined;
        last: string | undefined;
    };
    phone: string;
    constructionStarted: boolean | undefined;
    landSize: {
        quantity: string;
        unit: string;
    };
    shortTermRentalLease: boolean | undefined;
    generalContractorWillWork: boolean | undefined;
    generalContractorName: string;
    secondaryEmail: string | undefined;
    numberUnits: number | undefined;
    yearBuilt: number | undefined;
    undergroundStorageTanks: string | undefined;
    acquisitionDate: string | null;
    isForeclosure: boolean | undefined;
    needFundingAtPurchase: boolean | undefined;
    intendedWork: string | undefined;
    expectedListingPrice: number | undefined;
    targetLoanRequest: number | undefined;
    isAquisitionFinancing: boolean | undefined;
    anyExistingDebt: boolean | undefined;
    unitsIncomeRestricted: number | undefined;
    buildType: number | undefined;
    currentDebt: number | undefined;
    asIsValue: number | undefined;
    currentPlattedLots: number | undefined;
    plannedPlattedLots: number | undefined;
    requiredZoningOrEntitlement: boolean | undefined;
    describeRequiredInspections: string | undefined;
    willBuildOnLandYourself: boolean | undefined;
    exitPlans: string | undefined;
    startedSiteWork: boolean | undefined;
    hoaDues: boolean | undefined;
    expandingConditionedSqFt: boolean | undefined;
    makingStructuralChanges: boolean | undefined;
    preferredTermType: number | undefined;
    preferredInterestPaymentType: number | undefined;
    certificateOfOccupancy: number | undefined;
    proofOfOccupancy: IApplicationFile;
    workNeeded: boolean | undefined;
    workNeededDescription: string | undefined;
    propertyDevelopmentType: number | undefined;
    propertyDevelopmentOther: string | undefined;
    ownedThroughoutDevelopment: boolean | undefined;
    exitType: number | undefined;
    expectedTakeOutLoanAmount: number | undefined;
    dayOfWorkCompleted: string | null;
}

export interface IApplicationRental {
    purchasedLessThan2YearsAgo: boolean | undefined;
    purchaseContract: IApplicationFile;
    moreThan10kExpenditures: boolean | undefined;
    capitalExpenditures: IApplicationFile;
    currentDebt: number | undefined;
    provideRentalUnitsLater: boolean | undefined;
    numberRentalUnits: number | undefined;
    preferredTermType: number | undefined;
    monthsToAccessFunding: MonthsToAccessFundingType | undefined;
    targetLoanRequest: number | undefined;
    unitsIncomeRestricted: number | undefined;
    buildType: number | undefined;
    yearBuilt: number | undefined;
    isAquisitionFinancing: boolean | undefined;
    purchaseDate: string | undefined;
    isLoanProceedsUsedPropertyImprovements: boolean | undefined;
    buildRentTypeId: number | undefined;
    buildWorkDescriptionTypeId: number | undefined;
}

export interface IApplicationRentalUnits {
    guid: string;
    occupied: boolean | undefined;
    rent: number | undefined;
    squareFeet: number | undefined;
    bedrooms: number | undefined;
    bathrooms: number | undefined;
    garageBays: number | undefined;
}

export interface IApplicationFile {
    handleMode: AttachmentHandleMode;
    data?: string;
    uploadedFilename?: string;
    uploadedFileApiId?: string;
    savedFormId?: number;
}

export type FormType = 'Budget' | 'Land Budget' | 'Fix-Flip' | 'Fix-Flip-LOC' | 'Spec Sheet';

const toFormTypeId = (type: FormType | null): 1 | 2 | 3 | 4 => {
    switch (type) {
        case 'Budget':
            return 1;
        case 'Spec Sheet':
            return 2;
        case 'Fix-Flip':
        case 'Fix-Flip-LOC':
            return 3;
        case 'Land Budget':
            return 4;
        default:
            throw new Error(`Unrecognized form.type: ${type}`);
    }
};

type SavedFormUpdateDto = {
    id: number;
    formType: 1 | 2 | 3 | 4;
    form: string;
};

type FormRecordValueType = number | string | [string | null, number | null] | null;

export interface FormState {
    id: number | null | undefined;
    guid: string;
    propertyGuid: string;
    createdAt: Date;
    updatedAt: Date;
    // sharedFormId: number;
    type: FormType | null;
    loanType: LoanType | null;
    name: string | undefined;
    isDirty: boolean;
    values: Record<string, Record<string, FormRecordValueType>>;
}

export interface IBudgetConstruction {
    id: number;
    guid: string;
    type: FormType | null;
    name: string;
    propertyGuid: string;
    values: {
        'Interior Finishes': {
            Appliances: number;
            'Cabinets, Countertops': number;
            Drywall: number;
            Flooring: number;
            'Interior Trims, Doors and Mirrors': number;
            Lighting: number;
            Painting: number;
            'Plumbing Fixtures': number;
            Fireplaces: number;
            Insulation: number;
            'Other, Describe'?: [string, number];
        };

        'Site Work': {
            'Architecture, Engineering': number;
            'Building Permit Fees': number;
            'Clearing & Grading': number;
            'Well/Water': number;
            'Septic/Sewer': number;
            Demolition: number;
            Inspections: number;
            'Other, Describe'?: [string, number];
            Utilities: number;
        };

        Foundations: {
            'Excavations and Backfill': number;
            'Footings, Foundation and Concrete': number;
            'Other, Describe'?: [string, number];
            'Retaining Walls': number;
            Slabs: number;
        };

        'Foundations (Largest Subcontractor or Supplier)': {
            Name: string;
            Phone: string;
        };

        Framing: {
            Framing: number;
            'General Metal, Steel': number;
            Sheathing: number;
            Trusses: number;
            'Other, Describe'?: [string, number];
        };

        'Framing (Largest Subcontractor or Supplier)': {
            Name: string;
            Phone: string;
        };

        'Major Systems Rough-ins': {
            Electrical: number;
            Plumbing: number;
            HVAC: number;
            'Other, Describe'?: [string, number];
        };

        'Electrical (Largest Subcontractor or Supplier)': {
            Name: string;
            Phone: string;
        };

        'Plumbing (Largest Subcontractor or Supplier)': {
            Name: string;
            Phone: string;
        };

        'HVAC (Largest Subcontractor or Supplier)': {
            Name: string;
            Phone: string;
        };

        'Exterior Finishes': {
            'Exterior Wall Finish': number;
            'Garage Door': number;
            Roofing: number;
            'Windows & Doors': number;
            'Other, Describe'?: [string, number];
        };

        'Final Steps': {
            'Driveway & Walks': number;
            Landscaping: number;
            'Outdoor Structures': number;
            'Clean Up': number;
            'Other, Describe'?: [string, number];
        };

        'Land Cost': {
            'Land Cost': number;
            'Other, Describe'?: [string, number]; // This one is backwards from the rest, an old bug we may not be able to fix due to backwards compatability
        };
    };
}

export interface IBudgetFixFlip {
    id: number;
    guid: string;
    type: FormType | null;
    name: string;
    propertyGuid: string;
    values: {
        'Property Cost': {
            'Acquisition Price': number;
            'Initial Inspection': number;
            'Other Work': [string, number];
        };

        'Site Work': {
            Demolition: [string, number];
            'Building Permit Fees': [string, number];
            'Architecture, Engineering': [string, number];
            'Other Work': [string, number];
        };

        'Exterior Structure': {
            'Painting, Siding, Wall Finish': [string, number];
            'Guters or Fascia, Downspout': [string, number];
            'Porch or Deck': [string, number];
            Roofing: [string, number];
            'Windows and Doors': [string, number];
            'Garage Door': [string, number];
            'Other Work': [string, number];
        };

        'Major Systems': {
            Plumbing: [string, number];
            Electrical: [string, number];
            HVAC: [string, number];
            'Sewer or Septic': [string, number];
            'Other Work': [string, number];
        };

        Interior: {
            Insulation: [string, number];
            Drywall: [string, number];
            'Interior Trims, Doors, and Mirrors': [string, number];
            Lighting: [string, number];
            'Cabinets and Countertops': [string, number];
            Appliances: [string, number];
            Flooring: [string, number];
            'Plumbing Fixtures': [string, number];
            Fireplace: [string, number];
            'Other Work': [string, number];
        };

        'Expand Primary Structure': {
            'Item #1': [string, number];
            'Item #2': [string, number];
            'Item #3': [string, number];
            'Item #4': [string, number];
            'Item #5': [string, number];
        };

        'Final Structure': {
            Landscaping: [string, number];
            'Outdoor Structures': [string, number];
            'Driveway and Walks': [string, number];
            'Clean Up': [string, number];
            'Other Work': [string, number];
        };
    };
}

export interface IBudgetLand {
    id: number;
    guid: string;
    type: FormType | null;
    name: string;
    propertyGuid: string;
    values: {
        'Land Cost': {
            'Land Cost': number;
            'Other, Describe': [string, number];
        };
        Setup: {
            'Governmental Fees': number;
            Engineering: number;
            'Other, Describe': [string, number];
        };
        Utilities: {
            'Electric Lines': number;
            'Gas Lines': number;
            Sprinklers: number;
            'Telephone, Cable, and Fiber Optic Lines': number;
            'Other, Describe': [string, number];
        };
        'Site Work': {
            Demolition: number;
            Clearing: number;
            Grading: number;
            Paving: number;
            Sidewalks: number;
            Curbs: number;
            Gutters: number;
            Sewers: number;
            Landscaping: number;
            Hardscaping: number;
            'Other, Describe': [string, number];
        };
    };
}

export interface ISpecSheet {
    id: number;
    guid: string;
    type: FormType | null;
    name: string;
    propertyGuid: string;
    values: {
        'Additional Features': {
            Other: string;
            Bath: string;
            'Energy Savers': string;
            Kitchen: string;
            'Quality Assurance': string;
        };
        'Interior Features': {
            Appliances: string;
            Cabinets: string;
            Ceilings: string;
            Flooring: string;
            Doors: string;
            Others: string;
            Trim: string;
            Shelving: string;
            Lighting: string;
            Walls: string;
        };
        'Exterior Features': {
            Columns: string;
            'Driveway/Sidewalk': string;
            Doors: string;
            Other: string;
            Trim: string;
            'Garage/Overhead Door': string;
            'Gutters/Downspouts': string;
            Landscaping: string;
            'Porch, Deck, Patio': string;
            Roof: string;
            Shutters: string;
            Siding: string;
            Windows: string;
        };
    };
}

export interface ISaveApplicationResponse {
    entryGuid: string;
    token: string;
}

export interface ISaveApplicationFile {
    entryGuid?: string;
    loanType: LoanType;
    fileType: AttachmentType;
    propertyGuid?: string;
    file: File;
}

export interface ISaveApplicationFileResponse {
    applicationFileId: string;
    safeFileName: string;
}
export interface IGetTermsheetHtmlRequest {
    logoUrl: string;
    rentalExhibitImageUrl: string;
    loanType: LoanType;
    borrowerName: string;
    borrowerEmail: string;
    borrowerEntity: string;
    borrowerPhone: string;
    borrowerTitle: string;
    buildRentType: BuildRentType | undefined;
    preferredTermType: BuildRentType | undefined;
}

export interface DashboardLoansToClose {
    street: string;
    city: string;
}

export interface IAddress {
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    county?: string;
    country?: string;
}

export type EntityListResult = {
    result: [
        {
            entityId: number;
            entityName: string;
            entityStateIncorporated: string;
            entityType: number;
            masterNoteId?: number | undefined;
            address: IAddress;
        },
    ];
};

export enum ValidationMessageType {
    Error = 0,
    Warning = 1,
}

export type ValidationMessage = {
    messageType: ValidationMessageType;
    message: string;
};

export type ValidationMessagesResult = {
    hasErrors: boolean;
    hasWarnings: boolean;
    messages: [ValidationMessage];
};

export interface IEntityValidation {
    entityId: number;
    name: string;
    tin?: string;
    state: string;
}

export interface IHasSavedForms {
    hasSavedBudgets: boolean;
    savedSpecsheets: boolean;
}

export interface GCUsersByProperty {
    loanId: number;
    properties: GCUserProperty[];
    isPortfolio: boolean;
}

export interface GCUserProperty {
    propertyId: number;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    county?: string;
    gcUserId: string;
    gcName: string;
    role?: GCRoleType;
    // gcEntityId: number;
    // gcEntityName: string;
}

export interface InviteGCUserDto {
    email: string;
    displayRole?: GCRoleType;
    displayFirstName?: string;
    displayLastName?: string;
    displayPhone?: string;
    isApprovalRequired: boolean;
}

export interface AuthorizedDrawUserDto {
    id: number;
    userId: string;
    email?: string;
    displayRole?: GCRoleType;
    displayFirstName?: string;
    displayLastName?: string;
    displayPhone?: string;
    isApprovalRequired: boolean;
    gcAcceptedUtc?: string;
    createdDateUtc: string;
    userCreatedDate?: string;
    userLastLoginDate?: string;
    needsPasswordReset: boolean;
    hasPropertyAssignment: boolean;
    projectCount: number;
}

export interface SetDrawPermissionUserDto {
    propertyId: number;
    userId: string;
}

export interface UserExperienceDto {
    id: number;
    experienceTypeId: ExperienceType;
    projectTypeId: ExperienceProjectType;
    verificationStatusTypeId: ExperienceVerificationStatusType;
    isVerified: boolean;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    county: string;
    rowNumber: number;
}

export interface UserExperience {
    id: number;
    principalId: number;
    projectTypeId?: ExperienceProjectType;
    experienceTypeId: ExperienceType;
    propertyTypeId?: ExperiencePropertyType;
    twoToFourUnitTypeId?: ExperiencePropertyTwoToFourUnitStructureType;
    lender: string;
    address: IAddress;
    stillOwn: boolean;
    dateSold?: Date;
    dateSoldTypeId?: ExperienceDateType;
    salesPrice?: number;
    budget: number | undefined;
    unitCount: number | undefined;
    verificationStatusTypeId?: number;
    isVerified: boolean;
    commercialConsiderationAcknowledged?: boolean;
    homeConstructedAfterPurchase?: boolean;
    content: string;
}

export interface UserExperienceSaveResult {
    id: number;
    principalId: number;
    success: boolean;
}

export class ApplyApi extends BfiApi {
    dashboardApplications(): Promise<ApiResponse<IDashboardApplicationItem[]>> {
        return super.get(`/apply/dashboardApplications`);
    }

    userNotices(): Promise<ApiResponse<IUserNoticeCount>> {
        return super.get(`/notices/user/counts`);
    }

    getApplication(entryGuid: string, token: string | null): Promise<ApiResponse<IApplicationData>> {
        return super.get(`apply/GetApplication/${entryGuid}${token ? '?token=' + token : ''}`);
    }

    saveApplication(data: IApplicationData): Promise<ApiResponse<ISaveApplicationResponse>> {
        return super.post('apply/saveApplication', data);
    }

    deleteApplication(applicationId: number): Promise<IApiResponse> {
        return super.post(`apply/deleteApplication/${applicationId}`);
    }

    getAllBudgets(loanType: LoanType): Promise<ApiResponse<FormState[]>> {
        return super.get(`/apply/GetSavedForms/?loanType=${loanType}`);
    }

    hasSavedForms(loanType: LoanType): Promise<ApiResponse<IHasSavedForms>> {
        return super.get(`/apply/hasSavedForms/?loanType=${loanType}`);
    }

    updateSharedForm(form: IBudgetConstruction | IBudgetFixFlip | IBudgetLand | ISpecSheet): Promise<ApiResponse<number>> {
        const dto: SavedFormUpdateDto = {
            id: form.id ?? 0,
            formType: toFormTypeId(form.type),
            form: JSON.stringify(form),
        };

        return super.put('/apply/UpdateSharedForm', dto);
    }

    deleteSavedForm(id: number, type: FormType): Promise<ApiResponse<void>> {
        const formType = toFormTypeId(type);
        return super.post(`/apply/deleteSavedForm/${id}/${formType}`);
    }

    saveApplicationFile(data: ISaveApplicationFile): Promise<ApiResponse<ISaveApplicationFileResponse>> {
        const { entryGuid, file, fileType, propertyGuid, loanType } = data;

        const formData = new FormData();

        if (entryGuid) formData.append('entryGuid', entryGuid);

        formData.append('file', file, file.name);
        formData.append('fileType', fileType);

        if (propertyGuid) formData.append('propertyGuid', propertyGuid);

        formData.append('loanType', loanType.toString());

        return super.post('apply/saveApplicationFile/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    getTermSheetHtml(data: IGetTermsheetHtmlRequest): Promise<ApiResponse<string>> {
        return super.post('/templates/termsheets/sign', data);
    }

    dashboardLoansToClose(): Promise<ApiResponse<DashboardLoansToClose[][]>> {
        return super.get(`/apply/dashboardLoansToClose`);
    }

    getEntityList = async (): Promise<ApiResponse<EntityListResult>> => {
        return super.get(`/apply/getentitylist`);
    };

    getBorrowerInfo = async (): Promise<ApiResponse<IBorrowerInfo>> => {
        return super.get(`/apply/GetBorrowerInfo`);
    };

    validateEntity = async (data: IEntityValidation): Promise<ApiResponse<ValidationMessagesResult>> => {
        return super.post(`/apply/entities/validate`, data);
    };

    inviteDrawAuthorizedUser(dto: InviteGCUserDto): Promise<ApiResponse<AuthorizedDrawUserDto>> {
        return super.post(`/draw/inviteDrawAuthorizedUser`, dto);
    }

    resendDrawAuthorizedUserInvite(userId: string): Promise<ApiResponse<void>> {
        return super.post(`/draw/resendDrawAuthorizedUserInvite`, userId);
    }

    getDrawAuthorizedUsers(): Promise<ApiResponse<AuthorizedDrawUserDto[]>> {
        return super.get(`/draw/getDrawAuthorizedUsers`);
    }

    getDrawAuthorizedUser(id: number): Promise<ApiResponse<AuthorizedDrawUserDto>> {
        return super.get(`/draw/getDrawAuthorizedUser/${id}`);
    }

    updateDrawAuthorizedUser(dto: Partial<AuthorizedDrawUserDto> & { id: number }): Promise<ApiResponse<AuthorizedDrawUserDto>> {
        return super.put(`/draw/updateDrawAuthorizedUser`, dto);
    }

    deleteDrawAuthorizedUser(userId: string): Promise<ApiResponse<void>> {
        return super.delete(`/draw/deleteDrawAuthorizedUser/${userId}`);
    }

    updatePropertyDrawAuthorizedUser(data: SetDrawPermissionUserDto): Promise<ApiResponse<void>> {
        return super.post('draw/assignDrawPermissionUser', data);
    }

    unassignPropertyDrawAuthorizedUser(propertyId: number): Promise<ApiResponse<void>> {
        return super.delete(`draw/unassignDrawPermissionUser/${propertyId}`);
    }

    getAuthorizedUsers(): Promise<ApiResponse<AuthorizedDrawUserDto[]>> {
        return super.get(`/apply/authorizedUsers`);
    }

    getAssignedUsers(): Promise<ApiResponse<GCUsersByProperty[]>> {
        return super.get(`/apply/assignedUsers`);
    }

    getUnverifiedUserExperiences(): Promise<ApiResponse<UserExperienceDto[]>> {
        return super.get(`/experiences/apply/user/unverified`);
    }

    getVerifiedUserExperiences(): Promise<ApiResponse<UserExperienceDto[]>> {
        return super.get(`/experiences/apply/user/verified`);
    }

    getUserExperienceItem(id: number): Promise<ApiResponse<UserExperience>> {
        return super.get(`/experiences/apply/user/${id}`);
    }

    saveUserExperienceItem(userExperience: UserExperience): Promise<ApiResponse<UserExperienceSaveResult>> {
        return super.post(`/experiences/apply/user`, userExperience);
    }

    validateUserExperience(userExperience: UserExperience): Promise<ApiResponse<boolean>> {
        return super.post(`/experiences/apply/user/validate`, userExperience).then((response) => {
            const typed = response as ApiResponse<{ isValid: boolean }>;
            return {
                ...typed,
                result: Boolean(typed.result?.isValid),
            } as ApiResponse<boolean>;
        });
    }

    deleteUserExperienceItem(id: number): Promise<ApiResponse<void>> {
        return super.post(`/experiences/apply/user/delete/${id}`);
    }
}
